import React, { useCallback, useId, useMemo, useState } from "react";
import { useEntities } from "@hooks/useEntities";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { useActiveWorkspace } from "@hooks/useActiveWorkspace";
import { useCanPerformWorkspaceAction } from "@hooks/permissions/useCanPerformWorkspaceAction";
import { UserWorkspaceRole, WorkspaceAction } from "@common/userWorkspaceRoles";
import { useActiveWorkspaceRole } from "@hooks/permissions/useActiveWorkspaceRole";
import { useUpdateWorkspacePreferencesMutation } from "@api/workspace.api";
import { WORKSPACE_TIMEZONE_LABELS } from "@common/workspaceTimezone";
import { Switch } from "@design-system/Switch/Switch";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { LockIcon } from "@icons";
import { MultiLedgerSettingsSection } from "@components/settings/AccountingSettings/MultiLedgerSettingsSection";
import {
    AccrualLedgerSettingsRow,
    buildAccrualLedgerSettingsRows,
} from "@components/settings/AccountingSettings/accrualLedgerSettingsUtils";
import {
    AccrualLedgerSettingsColumn,
    AccrualLedgerSettingsView,
    AccrualLedgerSettingsViewSchema,
} from "@common/types/views/accrualLedgerSettingsView";
import { View, ViewDensity, ViewType } from "@common/types/views/views";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { createAccrualLedgerSettingsViewConfig } from "@components/settings/AccountingSettings/accrualLedgerSettingsViewConfig";
import { Section } from "@design-system/Section/Section";
import { useSettings } from "@components/settings/SettingsProvider";
import { Button } from "@design-system/Button/Button";
import {
    REPORT_SETTINGS_DRAWER_TITLE,
    REPORT_SETTINGS_ROW_DESCRIPTION,
    REPORT_SETTINGS_ROW_LABEL,
} from "@components/settings/ReportSettings/reportSettingsEditor";
import { AccountingMigrationsSection } from "./AccountingMigrations/AccountingMigrationsSection";
import { MercuryCoaTypeMappingModal } from "@components/accounting/chartOfAccounts/MercuryCoaTypeMappingModal";
import styles from "./AccountingSettings.module.scss";

export const AccountingSettings: React.FC = () => {
    const entities = useEntities({
        onlyBusiness: true,
        excludeMockEntities: true,
    });
    const {
        multiLedger,
        glFirstCategorization,
        workspaceTimezone,
        customReportSettings,
        mercuryCoaTypeMapping: isMercuryCoaTypeMappingEnabled,
    } = useTypedFlags();
    const workspace = useActiveWorkspace();
    const activeWorkspaceRole = useActiveWorkspaceRole();
    const canEditSettings = useCanPerformWorkspaceAction(
        WorkspaceAction.WORKSPACE_SETTINGS_EDIT,
    );
    const canShowReportSettings = customReportSettings && canEditSettings;
    const { mutate: updatePreferences } = useUpdateWorkspacePreferencesMutation(
        workspace?.id ?? "",
    );
    const { closeWithOverlay, open, path } = useSettings();
    const showAccountNumbersLabelId = useId();
    const classifyByCategoryLabelId = useId();

    const isShowAccountNumbers = workspace?.showAccountNumbers ?? true;
    const isAdmin =
        activeWorkspaceRole === UserWorkspaceRole.ADMIN ||
        activeWorkspaceRole === UserWorkspaceRole.PRIMARY_ADMIN;

    const handleShowAccountNumbersToggle = useCallback(() => {
        updatePreferences({
            showAccountNumbers: !isShowAccountNumbers,
        });
    }, [updatePreferences, isShowAccountNumbers]);

    // "Classify by category" is the inverse of GL-first: the toggle is active
    // when GL-first is OFF (glFirstEnabledAt == null). Flipping it is
    // forward-looking only and does not reclassify existing transactions.
    const isClassifyByCategory = workspace?.glFirstEnabledAt == null;

    const handleClassifyByCategoryToggle = useCallback(() => {
        updatePreferences({
            glFirstEnabled: workspace?.glFirstEnabledAt == null,
        });
    }, [updatePreferences, workspace?.glFirstEnabledAt]);

    const handleOpenReportSettingsEditor = useCallback(() => {
        if (!workspace) {
            return;
        }
        closeWithOverlay("reportSettingsEditor", {
            kind: "workspace",
            workspaceId: workspace.id,
            restoreSettings: () => open(path),
        });
    }, [closeWithOverlay, open, path, workspace]);

    const accrualLedgerViewConfig = useMemo(
        () => createAccrualLedgerSettingsViewConfig(),
        [],
    );
    const [accrualLedgerView, setAccrualLedgerView] =
        useState<AccrualLedgerSettingsView>({
            type: ViewType.ACCRUAL_LEDGER_SETTINGS,
            columns: [
                { id: AccrualLedgerSettingsColumn.ENTITY },
                {
                    id: AccrualLedgerSettingsColumn.ACTION,
                    options: { pin: "right" },
                },
            ],
            filters: {},
            options: { density: ViewDensity.SM },
        });

    const handleAccrualLedgerViewChange = useCallback((newView: View) => {
        setAccrualLedgerView(AccrualLedgerSettingsViewSchema.parse(newView));
    }, []);

    const accrualLedgerRows = useMemo<AccrualLedgerSettingsRow[]>(
        () => buildAccrualLedgerSettingsRows(entities),
        [entities],
    );

    const [showMercuryCoaTypeMapping, setShowMercuryCoaTypeMapping] =
        useState(false);

    return (
        <section>
            <header className="settings__header">
                <h3>Accounting</h3>
            </header>

            {workspace && (
                <Section heading="Preferences">
                    <article className={styles.preferencesSection}>
                        <div className={styles.preferencesRow}>
                            <div>
                                <header
                                    id={showAccountNumbersLabelId}
                                    className={styles.toggleLabel}
                                >
                                    Show account numbers
                                </header>
                                <p className={styles.toggleDescription}>
                                    Display account codes alongside account
                                    names throughout the app
                                </p>
                            </div>
                            <Switch
                                checked={isShowAccountNumbers}
                                onCheckedChange={handleShowAccountNumbersToggle}
                                disabled={!canEditSettings}
                                aria-labelledby={showAccountNumbersLabelId}
                            />
                        </div>

                        {glFirstCategorization && (
                            <div className={styles.preferencesRow}>
                                <div>
                                    <header
                                        id={classifyByCategoryLabelId}
                                        className={styles.toggleLabel}
                                    >
                                        Classify by category
                                    </header>
                                    <p className={styles.toggleDescription}>
                                        Use categories before GL accounts when
                                        classifying transactions - best for
                                        business insights
                                    </p>
                                </div>
                                <Switch
                                    checked={isClassifyByCategory}
                                    onCheckedChange={
                                        handleClassifyByCategoryToggle
                                    }
                                    disabled={!canEditSettings}
                                    aria-labelledby={classifyByCategoryLabelId}
                                />
                            </div>
                        )}

                        {workspaceTimezone && (
                            <div className={styles.preferencesRow}>
                                <div>
                                    <header className={styles.toggleLabel}>
                                        Timezone
                                    </header>
                                    <p className={styles.toggleDescription}>
                                        Transactions and journal entries are
                                        dated in this timezone. It can't be
                                        changed.
                                    </p>
                                </div>
                                <Tooltip content="Timezone can't be changed. It's locked to keep your books consistent.">
                                    <span className={styles.timezoneValue}>
                                        <LockIcon aria-hidden />
                                        {
                                            WORKSPACE_TIMEZONE_LABELS[
                                                workspace.timezone
                                            ]
                                        }
                                    </span>
                                </Tooltip>
                            </div>
                        )}

                        {canShowReportSettings && (
                            <div className={styles.preferencesRow}>
                                <div>
                                    <header className={styles.toggleLabel}>
                                        {REPORT_SETTINGS_ROW_LABEL}
                                    </header>
                                    <p className={styles.toggleDescription}>
                                        {REPORT_SETTINGS_ROW_DESCRIPTION}
                                    </p>
                                </div>
                                <Button
                                    variant="primary"
                                    size="md"
                                    onClick={handleOpenReportSettingsEditor}
                                >
                                    {REPORT_SETTINGS_DRAWER_TITLE}
                                </Button>
                            </div>
                        )}
                    </article>
                </Section>
            )}

            {multiLedger && (
                <MultiLedgerSettingsSection
                    entities={entities}
                    canEditSettings={canEditSettings}
                />
            )}

            {!multiLedger && (
                <Section heading="Accrual ledger">
                    {accrualLedgerRows.length === 0 ? (
                        <p>No business entities available.</p>
                    ) : (
                        <ViewTable
                            data={accrualLedgerRows}
                            view={accrualLedgerView}
                            config={accrualLedgerViewConfig}
                            onViewChange={handleAccrualLedgerViewChange}
                            rowBorders={false}
                            outerBorder
                        />
                    )}
                </Section>
            )}

            {isAdmin && workspace && (
                <AccountingMigrationsSection workspaceId={workspace.id} />
            )}

            {isMercuryCoaTypeMappingEnabled && (
                <>
                    <Section heading="Chart of accounts">
                        <article className={styles.preferencesSection}>
                            <div className={styles.preferencesRow}>
                                <div>
                                    <header className={styles.toggleLabel}>
                                        Map Mercury CoA types
                                    </header>
                                    <p className={styles.toggleDescription}>
                                        Upload a Mercury chart of accounts CSV
                                        and download it with Kick types mapped.
                                    </p>
                                </div>
                                <Button
                                    variant="primary"
                                    size="md"
                                    onClick={() =>
                                        setShowMercuryCoaTypeMapping(true)
                                    }
                                >
                                    Map CoA types
                                </Button>
                            </div>
                        </article>
                    </Section>

                    <MercuryCoaTypeMappingModal
                        show={showMercuryCoaTypeMapping}
                        onHide={() => setShowMercuryCoaTypeMapping(false)}
                    />
                </>
            )}
        </section>
    );
};
